import { useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import {
  useRelatedGroupMembers,
  useRelationshipTypes,
  type RelatedGroupMember,
} from '@/features/contact-groups/hooks/useContactGroups';

/**
 * GroupMemberJobFanout — shown on the Job create form. When the selected client
 * belongs to one or more groups, this lists the other members of those groups
 * and lets the user select which of them should ALSO get this job (one
 * individual job each).
 *
 * Crucially, it shows each member's EXISTING jobs so the user can decide whether
 * a new job is actually needed for that member.
 */
export function GroupMemberJobFanout({
  clientId,
  selectedIds,
  onChange,
  onDuplicatesChange,
  jobType,
  readOnly = false,
  hideHeader = false,
}: {
  clientId: string;
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  /** Reports the subset of SELECTED members that are potential duplicates. */
  onDuplicatesChange?: (ids: string[]) => void;
  /** The job type being created — used to flag members who already have an
   *  open job of the same type (potential duplicate). */
  jobType?: string;
  /** Read-only mode: hide checkboxes and the select-all action. */
  readOnly?: boolean;
  /** Hide the card header (when embedded inside another titled card). */
  hideHeader?: boolean;
}) {
  const { data: members, isLoading } = useRelatedGroupMembers(clientId || undefined);
  const { data: relTypes } = useRelationshipTypes();

  const relLabel = (code: string | null) =>
    code ? (relTypes?.find((t) => t.code === code)?.label ?? code) : null;

  // De-duplicate members that appear via multiple groups; merge their group
  // names for display and keep the union of their existing jobs.
  const uniqueMembers = useMemo(() => {
    const map = new Map<string, RelatedGroupMember & { group_names: string[] }>();
    for (const m of members ?? []) {
      const existing = map.get(m.contact_id);
      if (existing) {
        if (!existing.group_names.includes(m.group_name)) existing.group_names.push(m.group_name);
      } else {
        map.set(m.contact_id, { ...m, group_names: [m.group_name] });
      }
    }
    return Array.from(map.values());
  }, [members]);

  // Members who already have an OPEN job of the job type being created.
  const duplicateMemberIds = useMemo(() => {
    if (!jobType) return new Set<string>();
    return new Set(
      uniqueMembers
        .filter((m) => m.existing_jobs.some((j) => j.job_type === jobType))
        .map((m) => m.contact_id),
    );
  }, [uniqueMembers, jobType]);

  // If the client changes and previously-selected members are no longer valid,
  // drop them from the selection.
  useEffect(() => {
    if (!members) return;
    const validIds = new Set(uniqueMembers.map((m) => m.contact_id));
    const filtered = selectedIds.filter((id) => validIds.has(id));
    if (filtered.length !== selectedIds.length) onChange(filtered);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, members]);

  // Report the subset of SELECTED members that are duplicates, so the parent
  // can prompt for confirmation before creating jobs for them.
  useEffect(() => {
    const selectedDupes = selectedIds.filter((id) => duplicateMemberIds.has(id));
    onDuplicatesChange?.(selectedDupes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds, duplicateMemberIds]);

  if (!clientId) return null;
  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <p className="py-3 text-sm text-gray-500">Checking group members...</p>
        </CardContent>
      </Card>
    );
  }
  if (uniqueMembers.length === 0) {
    return (
      <Card>
        <CardContent>
          <p className="py-4 text-sm text-gray-500 dark:text-gray-400">
            This client isn&apos;t in a group with other members. Add them to a group (Contact →
            Groups tab) to create jobs for related members here.
          </p>
        </CardContent>
      </Card>
    );
  }

  const hasDuplicate = (m: RelatedGroupMember) => duplicateMemberIds.has(m.contact_id);

  const allSelected = uniqueMembers.every((m) => selectedIds.includes(m.contact_id));
  const toggleAll = () => onChange(allSelected ? [] : uniqueMembers.map((m) => m.contact_id));
  const toggleOne = (id: string) =>
    onChange(selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]);

  const list = (
    <ul role="list" className="divide-y divide-border dark:divide-border-dark">
      {uniqueMembers.map((m) => {
        const checked = selectedIds.includes(m.contact_id);
        return (
          <li key={m.contact_id} className="py-3">
            <label
              className={
                readOnly ? 'flex items-start gap-3' : 'flex cursor-pointer items-start gap-3'
              }
            >
              {!readOnly && (
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleOne(m.contact_id)}
                  className="mt-1 rounded border-gray-300"
                />
              )}
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {m.contact_name}
                  </span>
                  {m.relationship_type && (
                    <Badge variant="info" size="sm">
                      {relLabel(m.relationship_type)}
                    </Badge>
                  )}
                  {m.group_names.map((gn) => (
                    <Badge key={gn} variant="default" size="sm">
                      {gn}
                    </Badge>
                  ))}
                  {hasDuplicate(m) && (
                    <Badge variant="warning" size="sm">
                      Unfinished {jobType} job exists
                    </Badge>
                  )}
                </div>

                {/* Existing jobs for this member */}
                {m.existing_jobs.length === 0 ? (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    No existing jobs — a new job will be created.
                  </p>
                ) : (
                  <div className="mt-1">
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                      Existing jobs ({m.existing_jobs.length}):
                    </p>
                    <ul className="mt-0.5 space-y-0.5">
                      {m.existing_jobs.map((j) => (
                        <li
                          key={j.id}
                          className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300"
                        >
                          <span className="truncate">
                            {j.job_code ? `${j.job_code} · ` : ''}
                            {j.title}
                          </span>
                          <Badge variant="default" size="sm">
                            {j.status.replace(/_/g, ' ')}
                          </Badge>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </label>
          </li>
        );
      })}
    </ul>
  );

  // Embedded (no card header) — used when hosted inside another titled card.
  if (hideHeader) {
    return list;
  }

  return (
    <Card>
      <CardHeader
        title="Also create this job for group members"
        description="Members of this contact's group(s). Their existing jobs are shown so you can decide whether a new job is needed. Selected members each get their own copy of this job."
        action={
          readOnly ? undefined : (
            <button
              type="button"
              onClick={toggleAll}
              className="text-sm font-medium text-brand-600 hover:text-brand-700 dark:text-brand-400"
            >
              {allSelected ? 'Deselect all' : 'Select all'}
            </button>
          )
        }
      />
      <CardContent>{list}</CardContent>
    </Card>
  );
}
